import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Trash2, Pencil, X, Save, HandCoins, Settings2, CheckSquare, Square, DollarSign, UserPlus, Check, Ban, CalendarClock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { affiliatesAdminApi } from "../../lib/api";
import AffiliateDetailModal from "./AffiliateDetailModal";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const inputCls = "h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-[13px] text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 w-full";
const labelCls = "block text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5";
const KIND_LABEL = { first_purchase: "Subscription", subscription: "Subscription", credits: "AI credits", residual: "Residual renewal" };

export default function AffiliatesAdmin() {
  const [affiliates, setAffiliates] = useState([]);
  const [settings, setSettings] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editAff, setEditAff] = useState(null); // {} for new, object for edit
  const [savingSettings, setSavingSettings] = useState(false);
  const [selected, setSelected] = useState([]);
  const [filterAffiliate, setFilterAffiliate] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [payroll, setPayroll] = useState(null);
  const [processingPayroll, setProcessingPayroll] = useState(false);
  const [detailAffiliateId, setDetailAffiliateId] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [affs, sett, apps, pending] = await Promise.all([
        affiliatesAdminApi.list(), affiliatesAdminApi.getSettings(), affiliatesAdminApi.listApplications(),
        affiliatesAdminApi.payrollPending(),
      ]);
      setAffiliates(affs || []);
      setSettings(sett);
      setApplications(apps || []);
      setPayroll(pending);
    } catch (_) {
      toast.error("Could not load affiliates.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCommissions = useCallback(async () => {
    try {
      const params = {};
      if (filterAffiliate) params.affiliateId = filterAffiliate;
      if (filterStatus) params.status = filterStatus;
      const logs = await affiliatesAdminApi.listCommissions(params);
      setCommissions(logs || []);
      setSelected([]);
    } catch (_) {
      toast.error("Could not load commission log.");
    }
  }, [filterAffiliate, filterStatus]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadCommissions(); }, [loadCommissions]);

  const rejectApplication = async (app) => {
    if (!window.confirm(`Reject ${app.name}'s affiliate application?`)) return;
    try {
      await affiliatesAdminApi.updateApplication(app.id, { status: "rejected" });
      toast.success("Application rejected.");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reject application.");
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const saved = await affiliatesAdminApi.updateSettings({
        residualPct: Number(settings.residualPct),
        creditsDiscountValidityDays: Number(settings.creditsDiscountValidityDays),
        processingFeePct: Number(settings.processingFeePct),
        processingFeeFixedCents: Number(settings.processingFeeFixedCents),
      });
      setSettings(saved);
      toast.success("Program settings saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save settings.");
    } finally {
      setSavingSettings(false);
    }
  };

  const processPayroll = async () => {
    if (!payroll?.period?.payoutFriday) return;
    if (!window.confirm(`Approve & process payroll for the period paying out ${payroll.period.payoutFriday}? This marks all eligible commissions as paid.`)) return;
    setProcessingPayroll(true);
    try {
      const res = await affiliatesAdminApi.processPayroll(payroll.period.payoutFriday);
      toast.success(`Payroll processed: ${res.paidCount} commission row(s) paid${res.skippedCount ? `, ${res.skippedCount} skipped (inactive)` : ""}.`);
      loadAll();
      loadCommissions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not process payroll.");
    } finally {
      setProcessingPayroll(false);
    }
  };

  const delAff = async (id) => {
    if (!window.confirm("Delete this affiliate? Their coupon code will stop working immediately.")) return;
    try {
      await affiliatesAdminApi.remove(id);
      toast.success("Affiliate deleted.");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const markPaid = async () => {
    if (selected.length === 0) return;
    try {
      const res = await affiliatesAdminApi.markCommissionsPaid(selected);
      toast.success(`Marked ${res.markedCount} commission(s) as paid.`);
      loadCommissions();
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark as paid.");
    }
  };

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="h-7 w-7 text-cyan-400 animate-spin" /></div>;

  const pendingSelected = commissions.filter((c) => selected.includes(c.id));
  const pendingSelectedTotal = pendingSelected.reduce((sum, c) => sum + Number(c.commissionAmount || 0), 0);

  return (
    <div className="space-y-6" data-testid="platform-affiliates-admin">
      {/* Program Settings */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
        <div className="flex items-center gap-2 mb-1"><Settings2 className="h-4 w-4 text-amber-400" /><h3 className="text-[15px] font-bold text-slate-100">Program settings</h3></div>
        <p className="text-[12px] text-slate-400 mb-4">Global rules that apply to every affiliate. Individual commission/discount rates are set per-affiliate below.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Residual % (subscription renewals)</label>
            <input data-testid="affiliate-settings-residual" type="number" min="0" max="100" step="0.5" value={settings.residualPct}
              onChange={(e) => setSettings((s) => ({ ...s, residualPct: e.target.value }))} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>AI credit discount validity (days from signup)</label>
            <input data-testid="affiliate-settings-validity" type="number" min="0" step="1" value={settings.creditsDiscountValidityDays}
              onChange={(e) => setSettings((s) => ({ ...s, creditsDiscountValidityDays: e.target.value }))} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Credit card processing fee — %</label>
            <input data-testid="affiliate-settings-fee-pct" type="number" min="0" max="100" step="0.1" value={settings.processingFeePct}
              onChange={(e) => setSettings((s) => ({ ...s, processingFeePct: e.target.value }))} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Credit card processing fee — fixed (cents)</label>
            <input data-testid="affiliate-settings-fee-fixed" type="number" min="0" step="1" value={settings.processingFeeFixedCents}
              onChange={(e) => setSettings((s) => ({ ...s, processingFeeFixedCents: e.target.value }))} className={inputCls} />
          </div>
        </div>
        <p className="text-[11px] text-slate-500 mt-3">Applied to every payment before commission is calculated (e.g. {settings.processingFeePct}% + ${(Number(settings.processingFeeFixedCents || 0) / 100).toFixed(2)}), so payroll stays auditable.</p>
        <div className="flex justify-end mt-4">
          <button data-testid="affiliate-settings-save" onClick={saveSettings} disabled={savingSettings}
            className="inline-flex items-center gap-1.5 px-4 h-9 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-[13px] font-semibold disabled:opacity-50">
            {savingSettings ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save settings
          </button>
        </div>
      </div>

      {/* Weekly Payroll */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid="platform-payroll-section">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/10 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-amber-400" />
            <h3 className="text-[15px] font-bold text-slate-100">Payroll — upcoming Friday</h3>
            {payroll?.period && <span className="text-[12px] text-slate-500">{payroll.period.start} → {payroll.period.end} · paid out {payroll.period.payoutFriday}</span>}
          </div>
          <button
            data-testid="platform-payroll-process-btn"
            onClick={processPayroll}
            disabled={processingPayroll || !(payroll?.affiliates?.length)}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30 text-[13px] font-semibold disabled:opacity-40"
          >
            {processingPayroll ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Approve &amp; Process
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13px] min-w-[760px]">
            <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-white/[0.02]">
              <tr>
                <th className="px-5 py-2.5 font-semibold">Affiliate</th>
                <th className="px-3 py-2.5 font-semibold">Coupon</th>
                <th className="px-3 py-2.5 font-semibold text-right">Customers</th>
                <th className="px-3 py-2.5 font-semibold text-right">Gross revenue</th>
                <th className="px-3 py-2.5 font-semibold text-right">Processing fees</th>
                <th className="px-5 py-2.5 font-semibold text-right">Commission owed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {!payroll?.affiliates?.length ? (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-500">No pending payroll this period.</td></tr>
              ) : payroll.affiliates.map((a) => (
                <tr key={a.affiliateId} className="text-slate-200" data-testid={`platform-payroll-row-${a.affiliateId}`}>
                  <td className="px-5 py-3">
                    <button onClick={() => setDetailAffiliateId(a.affiliateId)} className="font-medium text-cyan-300 hover:underline">{a.affiliateName}</button>
                  </td>
                  <td className="px-3 py-3 font-mono text-slate-400">{a.couponCode}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{a.totals.customerCount}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-slate-400">{money(a.totals.grossRevenue)}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-rose-300/80">-{money(a.totals.processingFees)}</td>
                  <td className="px-5 py-3 text-right tabular-nums font-semibold text-emerald-300">{money(a.totals.commissionOwed)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Applications */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3.5 border-b border-white/10">
          <UserPlus className="h-4 w-4 text-amber-400" />
          <h3 className="text-[15px] font-bold text-slate-100">Affiliate applications</h3>
          {applications.filter((a) => a.status === "pending").length > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 text-[11px] font-semibold" data-testid="affiliate-applications-pending-count">
              {applications.filter((a) => a.status === "pending").length} pending
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13px] min-w-[760px]">
            <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-white/[0.02]">
              <tr>
                <th className="px-5 py-2.5 font-semibold">Applicant</th>
                <th className="px-3 py-2.5 font-semibold">Message</th>
                <th className="px-3 py-2.5 font-semibold">Applied</th>
                <th className="px-3 py-2.5 font-semibold text-center">Status</th>
                <th className="px-5 py-2.5 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {applications.length === 0 ? (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-500">No applications yet. Your "Become an Affiliate" footer link on the website will send them here.</td></tr>
              ) : applications.map((app) => (
                <tr key={app.id} className="text-slate-200" data-testid={`affiliate-app-row-${app.id}`}>
                  <td className="px-5 py-3">
                    <div className="font-medium">{app.name}</div>
                    <div className="text-[11.5px] text-slate-500">{app.email}{app.phone ? ` · ${app.phone}` : ""}</div>
                  </td>
                  <td className="px-3 py-3 max-w-[280px]"><div className="text-slate-400 line-clamp-2">{app.message || "—"}</div></td>
                  <td className="px-3 py-3 text-slate-400 whitespace-nowrap">{new Date(app.createdAt).toLocaleDateString()}</td>
                  <td className="px-3 py-3 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${app.status === "approved" ? "bg-emerald-500/15 text-emerald-300" : app.status === "rejected" ? "bg-rose-500/15 text-rose-300" : "bg-amber-500/15 text-amber-300"}`}>{app.status}</span>
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    {app.status === "pending" ? (
                      <>
                        <button data-testid={`affiliate-app-approve-${app.id}`} onClick={() => setEditAff({ name: app.name, email: app.email, phone: app.phone, applicationId: app.id })}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 text-[12px] font-semibold mr-1.5">
                          <Check className="h-3.5 w-3.5" /> Approve
                        </button>
                        <button data-testid={`affiliate-app-reject-${app.id}`} onClick={() => rejectApplication(app)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-rose-500/15 text-rose-300 hover:bg-rose-500/25 text-[12px] font-semibold">
                          <Ban className="h-3.5 w-3.5" /> Reject
                        </button>
                      </>
                    ) : <span className="text-slate-600 text-[12px]">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Affiliates table */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/10">
          <h3 className="text-[15px] font-bold text-slate-100">Affiliates</h3>
          <button data-testid="affiliate-new-btn" onClick={() => setEditAff({})}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-[13px] font-semibold">
            <Plus className="h-4 w-4" /> New affiliate
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13px] min-w-[960px]">
            <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-white/[0.02]">
              <tr>
                <th className="px-5 py-2.5 font-semibold">Name / Email</th>
                <th className="px-3 py-2.5 font-semibold">Coupon</th>
                <th className="px-3 py-2.5 font-semibold text-right">Subscription Comm. %</th>
                <th className="px-3 py-2.5 font-semibold text-right">Credits %</th>
                <th className="px-3 py-2.5 font-semibold text-right">Discount %</th>
                <th className="px-3 py-2.5 font-semibold text-right">Referrals</th>
                <th className="px-3 py-2.5 font-semibold text-right">Pending</th>
                <th className="px-3 py-2.5 font-semibold text-right">Paid</th>
                <th className="px-3 py-2.5 font-semibold text-center">Stripe</th>
                <th className="px-3 py-2.5 font-semibold text-center">Status</th>
                <th className="px-5 py-2.5 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {affiliates.length === 0 ? (
                <tr><td colSpan={11} className="px-5 py-8 text-center text-slate-500">No affiliates yet. Create one to start your referral program.</td></tr>
              ) : affiliates.map((a) => (
                <tr key={a.id} className="text-slate-200" data-testid={`affiliate-row-${a.id}`}>
                  <td className="px-5 py-3">
                    <button onClick={() => setDetailAffiliateId(a.id)} className="font-medium text-cyan-300 hover:underline" data-testid={`affiliate-name-link-${a.id}`}>{a.name}</button>
                    <div className="text-[11.5px] text-slate-500">{a.email}</div>
                  </td>
                  <td className="px-3 py-3 font-mono text-cyan-300">{a.couponCode}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{a.firstPurchaseCommissionPct}%</td>
                  <td className="px-3 py-3 text-right tabular-nums">{a.creditsCommissionPct}%</td>
                  <td className="px-3 py-3 text-right tabular-nums">{a.creditsDiscountPct}%</td>
                  <td className="px-3 py-3 text-right tabular-nums">{a.referralCount || 0}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-amber-300 font-semibold">{money(a.totalCommissionPending)}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-emerald-300">{money(a.totalCommissionPaid)}</td>
                  <td className="px-3 py-3 text-center">
                    <span
                      data-testid={`affiliate-stripe-badge-${a.id}`}
                      className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${a.stripe_authorization_code ? "bg-emerald-500/15 text-emerald-300" : "bg-white/5 text-slate-500"}`}
                    >
                      {a.stripe_authorization_code ? "Connected" : "Not Connected"}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${a.status === "active" ? "bg-emerald-500/15 text-emerald-300" : "bg-white/5 text-slate-500"}`}>{a.status}</span>
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <button data-testid={`affiliate-edit-${a.id}`} onClick={() => setEditAff(a)} className="p-1.5 rounded-md hover:bg-white/10 text-slate-400 hover:text-slate-200"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`affiliate-delete-${a.id}`} onClick={() => delAff(a.id)} className="p-1.5 rounded-md hover:bg-rose-500/10 text-slate-400 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Commission log / payouts */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/10 flex-wrap gap-3">
          <h3 className="text-[15px] font-bold text-slate-100">Commission log &amp; payouts</h3>
          <div className="flex items-center gap-2">
            <select data-testid="commission-filter-affiliate" value={filterAffiliate} onChange={(e) => setFilterAffiliate(e.target.value)}
              className="h-9 px-2.5 rounded-lg bg-[#1E2536] border border-white/10 text-[12.5px] text-slate-200">
              <option value="" style={{ backgroundColor: "#1E2536", color: "#e2e8f0" }}>All affiliates</option>
              {affiliates.map((a) => <option key={a.id} value={a.id} style={{ backgroundColor: "#1E2536", color: "#e2e8f0" }}>{a.name}</option>)}
            </select>
            <select data-testid="commission-filter-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
              className="h-9 px-2.5 rounded-lg bg-[#1E2536] border border-white/10 text-[12.5px] text-slate-200">
              <option value="" style={{ backgroundColor: "#1E2536", color: "#e2e8f0" }}>All statuses</option>
              <option value="pending" style={{ backgroundColor: "#1E2536", color: "#e2e8f0" }}>Pending</option>
              <option value="paid" style={{ backgroundColor: "#1E2536", color: "#e2e8f0" }}>Paid</option>
            </select>
            <button
              data-testid="commission-mark-paid-btn"
              onClick={markPaid}
              disabled={selected.length === 0}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30 text-[13px] font-semibold disabled:opacity-40"
            >
              <DollarSign className="h-4 w-4" /> Mark {selected.length > 0 ? `${selected.length} (${money(pendingSelectedTotal)})` : ""} as paid
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13px] min-w-[880px]">
            <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-white/[0.02]">
              <tr>
                <th className="px-5 py-2.5 font-semibold w-8"></th>
                <th className="px-3 py-2.5 font-semibold">Date</th>
                <th className="px-3 py-2.5 font-semibold">Affiliate</th>
                <th className="px-3 py-2.5 font-semibold">Company</th>
                <th className="px-3 py-2.5 font-semibold">Type</th>
                <th className="px-3 py-2.5 font-semibold text-right">Amount paid</th>
                <th className="px-3 py-2.5 font-semibold text-right">Rate</th>
                <th className="px-3 py-2.5 font-semibold text-right">Commission</th>
                <th className="px-5 py-2.5 font-semibold text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {commissions.length === 0 ? (
                <tr><td colSpan={9} className="px-5 py-8 text-center text-slate-500">No commissions logged yet.</td></tr>
              ) : commissions.map((c) => (
                <tr key={c.id} className="text-slate-200" data-testid={`commission-row-${c.id}`}>
                  <td className="px-5 py-3">
                    {c.status === "pending" ? (
                      <button data-testid={`commission-select-${c.id}`} onClick={() => toggleSelect(c.id)} className="text-slate-400 hover:text-cyan-300">
                        {selected.includes(c.id) ? <CheckSquare className="h-4 w-4 text-cyan-400" /> : <Square className="h-4 w-4" />}
                      </button>
                    ) : null}
                  </td>
                  <td className="px-3 py-3 text-slate-400 whitespace-nowrap">{new Date(c.createdAt).toLocaleDateString()}</td>
                  <td className="px-3 py-3">{c.affiliateName}</td>
                  <td className="px-3 py-3">{c.companyName}</td>
                  <td className="px-3 py-3 text-slate-300">{KIND_LABEL[c.kind] || c.kind}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-slate-400">{money(c.amountPaid)}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-slate-400">{c.ratePct}%</td>
                  <td className="px-3 py-3 text-right tabular-nums font-semibold text-emerald-300">{money(c.commissionAmount)}</td>
                  <td className="px-5 py-3 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${c.status === "paid" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>{c.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editAff && (
        <AffiliateModal affiliate={editAff} settings={settings} onClose={() => setEditAff(null)} onSaved={() => { setEditAff(null); loadAll(); }} />
      )}
      {detailAffiliateId && (
        <AffiliateDetailModal affiliateId={detailAffiliateId} onClose={() => setDetailAffiliateId(null)} />
      )}
    </div>
  );
}

function AffiliateModal({ affiliate, settings, onClose, onSaved }) {
  const feePct = settings?.processingFeePct ?? 0;
  const feeFixed = (Number(settings?.processingFeeFixedCents || 0) / 100).toFixed(2);
  const isNew = !affiliate.id;
  const [f, setF] = useState({
    name: affiliate.name || "",
    email: affiliate.email || "",
    phone: affiliate.phone || "",
    password: "",
    couponCode: affiliate.couponCode || "",
    firstPurchaseCommissionPct: affiliate.firstPurchaseCommissionPct ?? 10,
    creditsCommissionPct: affiliate.creditsCommissionPct ?? 10,
    creditsDiscountPct: affiliate.creditsDiscountPct ?? 10,
    status: affiliate.status || "active",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    if (!f.email.trim()) { toast.error("Email is required"); return; }
    if (!f.couponCode.trim()) { toast.error("Coupon code is required"); return; }
    if (isNew && f.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      const payload = {
        name: f.name.trim(),
        email: f.email.trim().toLowerCase(),
        phone: f.phone.trim(),
        couponCode: f.couponCode.trim().toUpperCase(),
        firstPurchaseCommissionPct: Number(f.firstPurchaseCommissionPct),
        creditsCommissionPct: Number(f.creditsCommissionPct),
        creditsDiscountPct: Number(f.creditsDiscountPct),
        status: f.status,
      };
      let created = null;
      if (isNew) {
        payload.password = f.password;
        created = await affiliatesAdminApi.create(payload);
      } else {
        if (f.password) payload.password = f.password;
        created = await affiliatesAdminApi.update(affiliate.id, payload);
      }
      if (affiliate.applicationId) {
        await affiliatesAdminApi.updateApplication(affiliate.applicationId, { status: "approved", affiliateId: created?.id });
      }
      toast.success(isNew ? "Affiliate created." : "Affiliate updated.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save affiliate.");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="affiliate-modal">
      <div className="w-full max-w-md rounded-2xl bg-[#0F1629] border border-white/10 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(ev) => ev.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h3 className="text-[15px] font-bold text-slate-100 inline-flex items-center gap-2"><HandCoins className="h-4 w-4 text-amber-400" /> {isNew ? "New affiliate" : "Edit affiliate"} {affiliate.applicationId && <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 normal-case">from application</span>}</h3>
          <button onClick={onClose} className="h-8 w-8 rounded-full hover:bg-white/10 flex items-center justify-center text-slate-400"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className={labelCls}>Name</label>
            <input data-testid="affiliate-form-name" value={f.name} onChange={(ev) => setF({ ...f, name: ev.target.value })} className={inputCls} placeholder="Jordan Reyes" />
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input data-testid="affiliate-form-email" type="email" value={f.email} onChange={(ev) => setF({ ...f, email: ev.target.value })} className={inputCls} placeholder="jordan@partner.com" />
          </div>
          <div>
            <label className={labelCls}>Phone <span className="text-slate-500 font-normal">(optional)</span></label>
            <input data-testid="affiliate-form-phone" value={f.phone} onChange={(ev) => setF({ ...f, phone: ev.target.value })} className={inputCls} placeholder="+1 555 123 4567" />
          </div>
          <div>
            <label className={labelCls}>{isNew ? "Password" : "New password (leave blank to keep)"}</label>
            <input data-testid="affiliate-form-password" type="password" value={f.password} onChange={(ev) => setF({ ...f, password: ev.target.value })} className={inputCls} placeholder="••••••••" />
          </div>
          <div>
            <label className={labelCls}>Coupon code</label>
            <input data-testid="affiliate-form-coupon" value={f.couponCode} onChange={(ev) => setF({ ...f, couponCode: ev.target.value.toUpperCase() })} className={`${inputCls} font-mono uppercase`} placeholder="JORDAN30" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>Subscription Comm. %</label>
              <input data-testid="affiliate-form-first-pct" type="number" min="0" max="100" step="0.5" value={f.firstPurchaseCommissionPct} onChange={(ev) => setF({ ...f, firstPurchaseCommissionPct: ev.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Credits comm. %</label>
              <input data-testid="affiliate-form-credits-pct" type="number" min="0" max="100" step="0.5" value={f.creditsCommissionPct} onChange={(ev) => setF({ ...f, creditsCommissionPct: ev.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Customer discount %</label>
              <input data-testid="affiliate-form-discount-pct" type="number" min="0" max="100" step="0.5" value={f.creditsDiscountPct} onChange={(ev) => setF({ ...f, creditsDiscountPct: ev.target.value })} className={inputCls} />
            </div>
          </div>
          <p className="text-[11px] text-slate-500 -mt-1">Referred customers get a one-time {f.creditsDiscountPct || 0}% discount on their first AI credit purchase; you earn {f.creditsCommissionPct || 0}% commission on that same purchase.</p>
          <div>
            <label className={labelCls}>Credit card processing fee <span className="text-slate-500 font-normal normal-case">(admin-set, Program Settings)</span></label>
            <div data-testid="affiliate-form-processing-fee" className="h-9 px-3 rounded-lg bg-white/[0.02] border border-white/10 text-[12.5px] text-slate-400 flex items-center">
              {feePct}% + ${feeFixed}
            </div>
          </div>
          <button onClick={() => setF({ ...f, status: f.status === "active" ? "inactive" : "active" })} data-testid="affiliate-form-status"
            className={`h-9 px-3 rounded-lg border text-[13px] font-semibold inline-flex items-center gap-1.5 ${f.status === "active" ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "bg-white/5 border-white/10 text-slate-400"}`}>
            {f.status === "active" ? "Active (coupon works)" : "Inactive (coupon disabled)"}
          </button>
          <div>
            <label className={labelCls}>Stripe Authorization Code <span className="text-slate-500 font-normal normal-case">(for future automated payouts)</span></label>
            <div data-testid="affiliate-form-stripe-code" className="h-9 px-3 rounded-lg bg-white/[0.02] border border-white/10 text-[12.5px] text-slate-400 font-mono flex items-center overflow-x-auto whitespace-nowrap">
              {affiliate.stripe_authorization_code || "Not connected"}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-white/10">
          <button onClick={onClose} className="px-4 h-9 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 text-[13px] font-medium">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="affiliate-form-save"
            className="inline-flex items-center gap-1.5 px-4 h-9 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-[13px] font-semibold disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {isNew ? "Create" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
